import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./form-style.css";
import Switch from "react-switch";
import IqamahCard from "../../components/common/IqamahCard/IqamahCard";
import NumberInput from "../../components/common/NumberInput/NumberInput";
import "./form-layout.css";
import { getTodayPrayerTimes } from "../../utils/prayer-times.service";
import {
  SabahuSettings,
  IQAMAH_MIN_AFTER_SABAHU,
  maxIqamahMinutes,
  maxSabahuMinutesFor,
  resolveSabahuTimes,
  minSabahuMinutesFor,
  readSabahuSettings,
  validateSabahu,
} from "../../utils/sabahu";

export interface FormDataStorage {
  name: string;
  location: string;
  mosqueName: string;
  attentionText: {
    title?: string;
    description?: string;
  };
  showIqamah: boolean;
  sabahu?: SabahuSettings;
  prayers: {
    [key: string]: {
      timeAfterAdhan?: number;
      timeBeforeSunRise?: number;
      showOnTV: boolean;
    };
  };
}

const FormPage = () => {
  const formData: FormDataStorage | null = JSON.parse(
    localStorage.getItem("formData") || "null"
  );

  const [name, setName] = useState(formData?.name || "");
  const [mosqueName, setMosqueName] = useState(formData?.mosqueName || "");
  const [location, setLocation] = useState(formData?.location || "");
  const [isIqamahOn, setIsIqamahOn] = useState(formData?.showIqamah || false);

  const [prayers] = useState<FormDataStorage["prayers"]>({
    dreka: formData?.prayers?.dreka || {
      timeAfterAdhan: 0,
      timeBeforeSunRise: 0,
      showOnTV: false,
    },
    ikindia: formData?.prayers?.ikindia || {
      timeAfterAdhan: 0,
      timeBeforeSunRise: 0,
      showOnTV: false,
    },
    akshami: formData?.prayers?.akshami || {
      timeAfterAdhan: 0,
      timeBeforeSunRise: 0,
      showOnTV: false,
    },
    jacia: formData?.prayers?.jacia || {
      timeAfterAdhan: 0,
      timeBeforeSunRise: 0,
      showOnTV: false,
    },
  });

  const initialSabahu = readSabahuSettings();
  const [sabahuEnabled, setSabahuEnabled] = useState(initialSabahu.enabled);
  const [sabahuMinutes, setSabahuMinutes] = useState(
    initialSabahu.minutesBeforeSunrise
  );
  const [sabahuIqamah, setSabahuIqamah] = useState(initialSabahu.iqamahMinutes);

  const navigate = useNavigate();

  // Live preview of today's times
  const todayTimes = getTodayPrayerTimes().todayPrayerTimes as any;
  const sunriseToday: string | undefined = todayTimes?.["L. e Diellit"];
  const imsakuToday: string | undefined = todayTimes?.Imsaku;
  const { adhan: sabahuAdhan, iqamah: sabahuIqamahTime } = resolveSabahuTimes(
    sunriseToday,
    imsakuToday,
    {
      enabled: true,
      minutesBeforeSunrise: sabahuMinutes,
      iqamahMinutes: sabahuIqamah,
    }
  );
  const sabahuMaxLimit = maxSabahuMinutesFor(sunriseToday, imsakuToday);

  const sabahuErrors = sabahuEnabled
    ? validateSabahu(sabahuMinutes, sabahuIqamah, sabahuMaxLimit)
    : [];

  // Numbers can never be pushed into an invalid range
  const sabahuMinLimit = minSabahuMinutesFor(sabahuIqamah);
  const iqamahMaxLimit = maxIqamahMinutes(sabahuMinutes);

  const changeSabahuMinutes = (delta: number) =>
    setSabahuMinutes((prev) =>
      Math.min(
        sabahuMaxLimit,
        Math.max(sabahuMinLimit, prev + delta)
      )
    );

  const changeSabahuIqamah = (delta: number) =>
    setSabahuIqamah((prev) =>
      Math.min(iqamahMaxLimit, Math.max(IQAMAH_MIN_AFTER_SABAHU, prev + delta))
    );

  const handleSubmit = (e: React.FormEvent) => {
    const formData: FormDataStorage | null = JSON.parse(
      localStorage.getItem("formData") || "null"
    );
    e.preventDefault();
    if (sabahuErrors.length > 0) return;
    const data: FormDataStorage = {
      name,
      location,
      mosqueName,
      attentionText: formData?.attentionText || { title: "", description: "" },
      prayers: formData?.prayers || prayers,
      showIqamah: isIqamahOn,
      sabahu: {
        enabled: sabahuEnabled,
        minutesBeforeSunrise: sabahuMinutes,
        iqamahMinutes: sabahuIqamah,
      },
    };
    localStorage.setItem("formData", JSON.stringify(data));
    setTimeout(() => {
      navigate("/");
    }, 0);
  };

  const toggleSwitch = () => {
    setIsIqamahOn((prev) => !prev);
  };

  const switchProps = {
    onColor: "#00B894",
    offColor: "#DFE6E9",
    handleDiameter: 24,
    uncheckedIcon: false,
    checkedIcon: false,
    height: 32,
    width: 56,
  };

  return (
    <div
      className="prefs-page"
      style={{
        backgroundImage: `url(${require("../../assets/images/background-opacity.png")}), url(${require(`../../assets/images/wallpaper/${new Date().getDate()}.jpeg`)})`,
      }}
    >
      <form onSubmit={handleSubmit} className="prefs-shell">
        <header className="prefs-header">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="prefs-back"
          >
            <i className="fas fa-chevron-left"></i> Kthehu
          </button>
          <h1>
            <i className="fas fa-mosque"></i> Preferencat e Xhamisë
          </h1>
          <button
            type="submit"
            className="prefs-save"
            disabled={sabahuErrors.length > 0}
          >
            <i className="fas fa-save"></i> Ruaj Ndryshimet
          </button>
        </header>

        <div className="prefs-grid">
          <section className="prefs-card prefs-wide">
            <h3>
              <i className="fas fa-info-circle"></i> Informacionet
            </h3>
            <div className="prefs-fields">
            <div className="form-group">
              <label htmlFor="name">
                <i className="fas fa-user-tie"></i> Imami
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Emri i imamit"
              />
            </div>
            <div className="form-group">
              <label htmlFor="mosqueName">
                <i className="fas fa-mosque"></i> Xhamia
              </label>
              <input
                type="text"
                id="mosqueName"
                value={mosqueName}
                onChange={(e) => setMosqueName(e.target.value)}
                placeholder="Emri i xhamisë"
              />
            </div>
            <div className="form-group">
              <label htmlFor="location">
                <i className="fas fa-map-marker-alt"></i> Lokacioni
              </label>
              <input
                type="text"
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Vendndodhja e xhamisë"
              />
            </div>
            </div>
          </section>

          <section
            className={`prefs-card ${sabahuErrors.length ? "prefs-invalid" : ""}`}
          >
            <h3>
              <i className="fas fa-sun"></i> Sabahu në vend të Imsakut
            </h3>
            <label className="prefs-switch">
              <span>Shfaq Sabahun (fsheh Imsakun)</span>
              <Switch
                checked={sabahuEnabled}
                onChange={setSabahuEnabled}
                {...switchProps}
              />
            </label>
            {sabahuEnabled ? (
              <div className="prefs-sabahu">
                <div className="prefs-step">
                  <div className="prefs-step-head">
                    <span>Ezani i Sabahut</span>
                    <strong className="prefs-time">{sabahuAdhan || "--:--"}</strong>
                  </div>
                  <p className="prefs-hint">
                    <b>{sabahuMinutes} min</b> para lindjes së diellit
                  </p>
                  <NumberInput
                    minutes={sabahuMinutes}
                    incrementMinutes={() => changeSabahuMinutes(1)}
                    decrementMinutes={() => changeSabahuMinutes(-1)}
                    disableDecrement={sabahuMinutes <= sabahuMinLimit}
                    disableIncrement={sabahuMinutes >= sabahuMaxLimit}
                  />
                </div>
                <div className="prefs-step">
                  <div className="prefs-step-head">
                    <span>Ikameti</span>
                    <strong className="prefs-time">
                      {sabahuIqamahTime || "--:--"}
                    </strong>
                  </div>
                  <p className="prefs-hint">
                    <b>{sabahuIqamah} min</b> pas Ezanit
                  </p>
                  <NumberInput
                    minutes={sabahuIqamah}
                    incrementMinutes={() => changeSabahuIqamah(1)}
                    decrementMinutes={() => changeSabahuIqamah(-1)}
                    disableDecrement={sabahuIqamah <= IQAMAH_MIN_AFTER_SABAHU}
                    disableIncrement={sabahuIqamah >= iqamahMaxLimit}
                  />
                </div>
                {sunriseToday && (
                  <p className="prefs-hint prefs-sunrise">
                    Imsaku sot: <b>{imsakuToday}</b> · Lindja e diellit:{" "}
                    <b>{sunriseToday}</b>
                  </p>
                )}
                {sabahuErrors.length > 0 && (
                  <div className="prefs-error" role="alert">
                    {sabahuErrors.map((msg) => (
                      <p key={msg}>⚠ {msg}</p>
                    ))}
                    <p>Ndryshimet nuk ruhen derisa të korrigjohet.</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="prefs-hint">
                Kur është e fikur, në TV shfaqet Imsaku.
              </p>
            )}
          </section>

          <section className="prefs-card">
            <h3>
              <i className="fas fa-clock"></i> Ikameti
            </h3>
            <label className="prefs-switch">
              <span>Shfaq Ikametin në TV</span>
              <Switch
                checked={isIqamahOn}
                onChange={toggleSwitch}
                {...switchProps}
              />
            </label>
            {isIqamahOn && (
              <div className="prefs-iqamah-grid">
                <IqamahCard prayer="dreka" />
                <IqamahCard prayer="ikindia" />
                <IqamahCard prayer="akshami" />
                <IqamahCard prayer="jacia" />
              </div>
            )}
          </section>
        </div>
      </form>
    </div>
  );
};

export default FormPage;
